import { useAuth0 } from '@auth0/auth0-react';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { history } from '~/constants';
import constVars from '~/constants/constVars';
import { AuthProvider, StepStatus } from '~/enums';
import { accessToken } from '~/store/apis/utils/accessToken';
import { captureAuth0AuthStep } from '~/helpers/auth0Telemetry';
import { AuthContext, IAuthContext } from './AuthContext';

export const Auth0AuthProvider = ({
  children,
  hasIsAuthenticatedCookie,
}: {
  children: ReactNode;
  hasIsAuthenticatedCookie: boolean;
}) => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: auth0IsLoading,
    logout: auth0Logout,
    loginWithRedirect,
  } = useAuth0();

  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    isLoading: true,
  });

  const signInWithRedirect = useCallback(
    (
      options: Parameters<NonNullable<IAuthContext['signInWithRedirect']>>[0]
    ) => {
      captureAuth0AuthStep({
        stepName: 'interactive_login',
        stepStatus: StepStatus.Started,
        properties: {
          connection: options.connection,
          market: options.market,
          returnTo: options.returnTo,
        },
        sendInstantly: true,
      });

      return loginWithRedirect({
        authorizationParams: {
          connection: options.connection,
          language: options.language,
          market: options.market,
          ...(options.redirectUri && {
            redirect_uri: options.redirectUri,
          }),
        },
        appState: { returnTo: options.returnTo },
      }).catch((error) => {
        captureAuth0AuthStep({
          stepName: 'interactive_login',
          stepStatus: StepStatus.Error,
          properties: {
            connection: options.connection,
            market: options.market,
          },
        });
        throw error;
      });
    },
    [loginWithRedirect]
  );

  const logout = useCallback(() => {
    return auth0Logout({
      logoutParams: {
        returnTo: `${globalThis.location.origin}/${constVars.ROUTE_LOGIN}`,
      },
    });
  }, []);

  const auth = useMemo<IAuthContext>(
    () => ({
      provider: AuthProvider.AUTH0,
      ...authState,
      logout,
      signInWithRedirect,
    }),
    [authState, logout, signInWithRedirect]
  );

  const fetchAuth0AccessToken = async (): Promise<string> => {
    return getAccessTokenSilently();
  };

  useEffect(() => {
    const handleAuthenticationStatus = async () => {
      const url = new URL(globalThis.location.href);
      const errorParam = url.searchParams.get('error');
      const hasLoginError = errorParam === 'login_required';

      if (hasLoginError) {
        captureAuth0AuthStep({
          stepName: 'silent_reauth',
          stepStatus: StepStatus.Error,
          properties: { pathname: globalThis.location.pathname },
        });

        url.searchParams.delete('error');
        url.searchParams.delete('error_description');
        url.searchParams.delete('state');
        history.replace({
          search: url.search,
        });
      }

      if (!isAuthenticated && hasIsAuthenticatedCookie && !hasLoginError) {
        captureAuth0AuthStep({
          stepName: 'silent_reauth',
          stepStatus: StepStatus.Started,
          properties: {
            returnTo: `${globalThis.location.pathname}${globalThis.location.search}`,
            pathname: globalThis.location.pathname,
            urlHasCode: url.searchParams.has('code'),
            urlHasState: url.searchParams.has('state'),
          },
          sendInstantly: true,
        });

        sessionStorage.setItem(
          'returnTo',
          `${globalThis.location.pathname}${globalThis.location.search}`
        );

        return await loginWithRedirect({
          authorizationParams: {
            prompt: 'none',
          },
          appState: {
            returnTo: `${globalThis.location.pathname}${globalThis.location.search}`,
          },
        });
      } else if (isAuthenticated) {
        captureAuth0AuthStep({
          stepName: 'session_authenticated',
          stepStatus: StepStatus.Finished,
          properties: { pathname: globalThis.location.pathname },
        });

        accessToken.set(fetchAuth0AccessToken);
      }

      setAuthState({ isAuthenticated, isLoading: auth0IsLoading });
    };

    if (!auth0IsLoading) {
      handleAuthenticationStatus();
    }
  }, [isAuthenticated, auth0IsLoading]);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
